import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}




import {
  morphSection as snowyMorphSection,
  sectionRenderer as snowySectionRenderer,
} from '@theme/section-renderer';

import {
  CartLinesUpdateEvent as SnowyCartLinesUpdateEvent,
} from '@shopify/events';

let snowyProtectionBusy = false;

document.addEventListener('change', async (event) => {
  const checkbox = event.target;

  if (
    !(checkbox instanceof HTMLInputElement) ||
    !checkbox.matches('[data-snowy-protection-checkbox]')
  ) {
    return;
  }

  if (snowyProtectionBusy) {
    checkbox.checked = !checkbox.checked;
    return;
  }

  const component = checkbox.closest('cart-items-component');
  const sectionId = component?.sectionId;
  const box = checkbox.closest('[data-snowy-protection]');
  const message = box?.querySelector('[data-snowy-protection-message]');
  const wanted = checkbox.checked;
  const root = window.Shopify?.routes?.root || '/';
  const variantId = 67919150383348;

  if (!component || !sectionId) {
    checkbox.checked = !wanted;
    if (message) {
      message.textContent = 'Unable to find the cart section.';
    }
    return;
  }

  snowyProtectionBusy = true;
  checkbox.disabled = true;

  const checkoutButtons = Array.from(
    document.querySelectorAll('[name="checkout"]')
  );
  const previousStates = checkoutButtons.map(button => button.disabled);

  checkoutButtons.forEach(button => {
    button.disabled = true;
  });

  if (message) message.textContent = 'Updating…';

  let cartChanged = false;

  async function cartRequest(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const data = await response.json();

    if (!response.ok || data.errors) {
      throw new Error(
        data.description ||
        data.message ||
        'Unable to update shipping protection.'
      );
    }

    return data;
  }

  try {
    const cart = await cartRequest('cart.js');
    const matchingItems = cart.items.filter(
      item => item.variant_id === variantId
    );

    if (
      wanted &&
      !cart.items.some(item => item.variant_id !== variantId)
    ) {
      throw new Error('Add a product before adding protection.');
    }

    const sectionOptions = {
      sections: [sectionId],
      sections_url: window.location.pathname,
    };

    let result;

    if (matchingItems.length) {
      const updates = {};

      matchingItems.forEach((item, index) => {
        updates[item.key] = wanted && index === 0 ? 1 : 0;
      });

      result = await cartRequest('cart/update.js', {
        updates,
        ...sectionOptions,
      });
      cartChanged = true;
    } else if (wanted) {
      result = await cartRequest('cart/add.js', {
        items: [{ id: variantId, quantity: 1 }],
        ...sectionOptions,
      });
      cartChanged = true;
    } else {
      checkbox.disabled = false;
      if (message) message.textContent = '';
      return;
    }

    const sectionHTML = result.sections?.[sectionId];

    if (!sectionHTML) {
      throw new Error('The cart changed, but its display could not update.');
    }

    const currentCart = await cartRequest('cart.js');

    const completion = Promise.resolve({
      cart: SnowyCartLinesUpdateEvent.createCartFromAjaxResponse(
        currentCart
      ),
      detail: {
        sections: result.sections,
        items: currentCart.items,
        itemCount: currentCart.item_count,
        source: 'cart-items-component',
        didError: false,
      },
    });

    component.dispatchEvent(
      new SnowyCartLinesUpdateEvent({
        action: wanted ? 'add' : 'remove',
        context: 'cart',
        lines: [{
          id: String(variantId),
          quantity: wanted ? 1 : 0,
        }],
        promise: completion,
      })
    );

    snowySectionRenderer.abortRender(sectionId);

    await snowyMorphSection(sectionId, sectionHTML, {
      mode: component.isDrawer ? 'hydration' : 'full',
    });
  } catch (error) {
    if (!cartChanged) checkbox.checked = !wanted;

    checkbox.disabled = cartChanged;

    if (message) {
      message.textContent = cartChanged
        ? 'Your cart changed, but the display could not update. Refresh before continuing.'
        : error.message || 'Unable to update protection. Please try again.';
    }
  } finally {
    snowyProtectionBusy = false;

    checkoutButtons.forEach((button, index) => {
      if (button.isConnected) {
        button.disabled = previousStates[index];
      }
    });
  }
});
